import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../../../services/api';
import useAuthStore from '../../../stores/useAuthStore';
import useCartStore from '../../../stores/useCartStore';

import DesktopNav from './DesktopNav';
import UserMenu from './UserMenu';
import MobileMenu from './MobileMenu';

// user thay đổi => header tự động re-render
const Header = () => {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const getItemCount = useCartStore((state) => state.getItemCount);

  const [cartCount, setCartCount] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState({});
  const navigate = useNavigate();

  const normalizeText = (value = '') => {
    const text = String(value ?? '');
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/đ/g, 'd')
      .trim();
  };

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const MIN_Y = 80;
    const THRESHOLD = 8;

    const update = () => {
      const y = window.scrollY;
      const diff = y - lastY;

      // Khi đang mở menu mobile, luôn giữ header hiện để tránh UI bị mất.
      if (isMobileMenuOpen) {
        setIsHeaderHidden(false);
      } else {
        if (y > MIN_Y && diff > THRESHOLD) {
          setIsHeaderHidden(true);
        } else if (diff < -THRESHOLD || y <= MIN_Y) {
          setIsHeaderHidden(false);
        }
      }

      lastY = y;
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    // Update cart count using Zustand store
    const updateCartCountFromStore = () => {
      const cartCount = getItemCount();
      setCartCount(cartCount);
    };

    updateCartCountFromStore();

    // Fetch categories and subcategories
    fetchCategoriesAndSubcategories();

    // Listen for storage changes (cart updates)
    const handleStorageChange = () => {
      updateCartCountFromStore();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [getItemCount]);

  useEffect(() => {
    // Lắng nghe custom event 'cart-updated' để cập nhật số lượng khi thêm/xóa
    const handleCartUpdated = () => {
      const cartCount = getItemCount();
      setCartCount(cartCount);
    };
    window.addEventListener('cart-updated', handleCartUpdated);
    return () => window.removeEventListener('cart-updated', handleCartUpdated);
  }, [getItemCount]);

  const updateCartCount = () => {
    const cartCount = getItemCount();
    setCartCount(cartCount);
  };

  const hasFetchedRef = useRef(false);

  const fetchCategoriesAndSubcategories = async () => {
    if (categories.length > 0 || hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    try {
      const categoriesResponse = await apiService.categories.getAll();
      const categoriesData = categoriesResponse.data.data || [];
      setCategories(categoriesData);

      const subcategoriesData = {};
      await Promise.all(categoriesData.map(async (category) => {
        try {
          const subRes = await apiService.subcategories.getByCategory(category._id);
          subcategoriesData[category._id] = subRes.data.data || [];
        } catch (err) {
          subcategoriesData[category._id] = [];
        }
      }));
      
      setSubcategories(subcategoriesData);
    } catch (error) {
      console.error('Error fetching menu data:', error);
      hasFetchedRef.current = false;
    }
  };

  const handleLogout = () => {
    logout();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      closeMobileMenu();
    }
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsHeaderHidden(false);
    setExpandedCategories({});
  };

  const handleCartIconClick = () => {
    updateCartCount();
  };

  return (
    <header className={`fixed top-0 w-full bg-white text-slate-900 border-b border-slate-200 z-[9999] shadow-md transition-transform duration-300 will-change-transform ${isHeaderHidden ? '-translate-y-full' : 'translate-y-0'}`}>
      <div className="container mx-auto px-4 relative">
        <div className="flex justify-between items-center py-2.5 sm:py-3">
          
          {/* CỘT 1: LOGO */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2 pl-2 sm:pl-4">
              <img
                src="/images/den_hai_dang_lao_cai_den-hai-dang-logo.webp"
                alt="Đèn Hải Đăng"
                className="h-10 sm:h-12 w-auto object-contain"
              />
            </Link>
          </div>

          {/* CỘT 2: MENU ĐIỀU HƯỚNG DESKTOP */}
          <DesktopNav
            categories={categories}
            subcategories={subcategories}
            normalizeText={normalizeText}
          />

          {/* CỘT 3: CÁC TIỆN ÍCH */}
          <div className="flex items-center justify-end space-x-4 xl:space-x-6 flex-shrink-0">
            
            {/* Thanh Tìm Kiếm Rộng */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center w-[200px] lg:w-[250px] xl:w-[320px]">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Tìm kiếm đèn chùm, quạt trần, đèn hiện đại..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-full bg-white text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/40 focus:border-yellow-500 text-xs transition-all duration-300 shadow-sm"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </form>

            {/* Giỏ Hàng */}
            <Link to="/cart" className="relative p-2 text-slate-900 hover:text-yellow-700 transition-colors" onClick={handleCartIconClick}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 6.75h1.5l1.5 9a2.25 2.25 0 002.25 1.875h7.5a2.25 2.25 0 002.25-1.875l1.5-9h-15zM7.5 21a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm9 0a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
              </svg>
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-yellow-500 text-slate-950 text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center border-2 border-white shadow-sm">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>

            {/* Dropdown Tài Khoản */}
            <UserMenu
              user={user}
              handleLogout={handleLogout}
            />

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(prev => !prev)}
              className="lg:hidden p-2 text-gray-900 hover:text-yellow-600 transition-colors duration-200"
              aria-label={isMobileMenuOpen ? "Đóng menu" : "Mở menu"}
            >
              {isMobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile navigation drawer component */}
      <MobileMenu
        isMobileMenuOpen={isMobileMenuOpen}
        closeMobileMenu={closeMobileMenu}
        categories={categories}
        subcategories={subcategories}
        expandedCategories={expandedCategories}
        toggleCategoryExpansion={toggleCategoryExpansion}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearch={handleSearch}
        user={user}
        handleLogout={handleLogout}
      />
    </header>
  );
};

export default Header;
